<template>
  <div class="ui-input-wrap">
    <label v-if="label" :for="inputId" class="ui-input__label">{{ label }}</label>
    <div
      class="ui-input__field"
      :class="{
        'ui-input__field--focused': isFocused,
        'ui-input__field--error': !!error,
        'ui-input__field--disabled': disabled,
      }"
    >
      <span v-if="hasIconLeft" class="ui-input__icon ui-input__icon--left" aria-hidden="true">
        <slot name="icon-left"></slot>
      </span>
      <input
        :id="inputId"
        :name="name"
        :type="currentType"
        :value="value"
        :placeholder="placeholder"
        :disabled="disabled"
        :required="required"
        :autocomplete="autocomplete"
        :aria-invalid="!!error ? 'true' : undefined"
        :aria-describedby="ariaDescribedBy"
        :aria-required="required ? 'true' : undefined"
        class="ui-input__native"
        @input="$emit('input', $event.target.value)"
        @focus="isFocused = true"
        @blur="isFocused = false"
      />
      <span v-if="hasIconRight && type !== 'password'" class="ui-input__icon ui-input__icon--right" aria-hidden="true">
        <slot name="icon-right"></slot>
      </span>
      <button
        v-if="type === 'password'"
        type="button"
        class="ui-input__eye-toggle"
        :aria-label="showPassword ? 'הסתר סיסמה' : 'הצג סיסמה'"
        @click="showPassword = !showPassword"
      >
        <svg v-if="showPassword" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
          <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
          <line x1="1" y1="1" x2="23" y2="23"/>
        </svg>
        <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
          <circle cx="12" cy="12" r="3"/>
        </svg>
      </button>
    </div>
    <p v-if="error" :id="errorId" class="ui-input__error">{{ error }}</p>
    <p v-else-if="hint" :id="hintId" class="ui-input__hint">{{ hint }}</p>
  </div>
</template>

<script>
let uid = 0;
export default {
  name: 'UiInput',
  model: {
    prop: 'value',
    event: 'input',
  },
  props: {
    value: { type: String, default: '' },
    type: {
      type: String,
      default: 'text',
      validator: v => ['text', 'email', 'tel', 'password', 'number'].includes(v),
    },
    label: { type: String, default: '' },
    hint: { type: String, default: '' },
    error: { type: String, default: '' },
    placeholder: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
    required: { type: Boolean, default: false },
    autocomplete: { type: String, default: undefined },
    name: { type: String, default: undefined },
    id: { type: String, default: '' },
  },
  data() {
    uid++;
    const base = this.id || `ui-input-${uid}`;
    return {
      isFocused: false,
      showPassword: false,
      inputId: base,
      hintId: `${base}-hint`,
      errorId: `${base}-error`,
    };
  },
  computed: {
    currentType() {
      if (this.type === 'password') {
        return this.showPassword ? 'text' : 'password';
      }
      return this.type;
    },
    hasIconLeft() {
      return !!this.$slots['icon-left'];
    },
    hasIconRight() {
      return !!this.$slots['icon-right'];
    },
    ariaDescribedBy() {
      if (this.error) return this.errorId;
      if (this.hint) return this.hintId;
      return undefined;
    },
  },
};
</script>

<style scoped lang="scss">
.ui-input-wrap {
  display: flex;
  flex-direction: column;
}

.ui-input__label {
  display: block;
  font-size: var(--text-xs);
  font-weight: var(--weight-semibold);
  color: var(--color-textMuted);
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-block-end: var(--space-2);
}

.ui-input__field {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 48px;
  padding-inline: 14px;
  background: var(--color-surface2);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-sm);
  transition: border-color var(--duration-fast) var(--easing-standard),
              box-shadow var(--duration-fast) var(--easing-standard);
}

.ui-input__field--focused {
  border-color: var(--color-text);
  box-shadow: var(--shadow-glow);
}

.ui-input__field--error {
  border-color: var(--color-danger);
}

.ui-input__field--disabled {
  opacity: 0.5;
}

.ui-input__native {
  flex: 1;
  min-width: 0;
  background: transparent;
  border: none;
  outline: none;
  color: var(--color-text);
  font-size: 15px;
  font-family: var(--font-sans);
  caret-color: var(--color-text);
}

.ui-input__native::placeholder {
  color: var(--color-textDim);
}

.ui-input__native:disabled {
  cursor: not-allowed;
}

.ui-input__icon {
  display: inline-flex;
  align-items: center;
  color: var(--color-textDim);
  flex-shrink: 0;
  font-size: 18px;
  width: 18px;
  height: 18px;
}

.ui-input__eye-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 44px;
  min-height: 44px;
  margin-inline-end: -10px;
  background: none;
  border: none;
  color: var(--color-textMuted);
  cursor: pointer;
  flex-shrink: 0;
  border-radius: var(--radius-sm);
}

.ui-input__eye-toggle:focus-visible {
  outline: 2px solid var(--color-accent);
  outline-offset: 2px;
}

.ui-input__hint {
  margin: 0;
  margin-block-start: 6px;
  font-size: var(--text-xs);
  color: var(--color-textDim);
  line-height: var(--leading-snug);
}

.ui-input__error {
  margin: 0;
  margin-block-start: 6px;
  font-size: var(--text-xs);
  color: var(--color-danger);
  line-height: var(--leading-snug);
}

@media (prefers-reduced-motion: reduce) {
  .ui-input__field {
    transition: none;
  }
}
</style>
